import React, { useCallback } from 'react'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import RemoveIcon from '@mui/icons-material/Remove'
import styled, { keyframes } from 'styled-components'
import { useDragOperation } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'

import Tooltip from 'components/Tooltip'

import { CardConfig, CardId } from 'pages/Dashboard/cardRegistry'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  id: CardId
  config: CardConfig
  isCustomizing: boolean
  isFlashing?: boolean
  onRemoveCallback: (id: CardId) => void
}

const DraggableCard = ({ id, config: { Component, colSpan }, isCustomizing, isFlashing = false, onRemoveCallback }: Props) => {
  const sortable = useSortable({
    id,
    index: 0,
    disabled: !isCustomizing,
    transition: null
  })
  const { source, target } = useDragOperation()

  const isOverTarget = target?.id === id && source?.id !== id

  const onRemove = useCallback(() => {
    onRemoveCallback(id)
  }, [id, onRemoveCallback])

  return (
    <S.Wrapper ref={sortable.ref} data-card-id={id} tabIndex={-1} $colSpan={colSpan} $isDragging={sortable.isDragging} $isFlashing={isFlashing}>
      {isOverTarget && <S.DropIndicator />}
      <S.CardSlot>
        <Component />
      </S.CardSlot>
      {isCustomizing && (
        <S.Controls>
          <Tooltip title="Click and drag to reorder this card">
            <S.DragHandle
              ref={sortable.handleRef}
              type="button"
              aria-label={`Reorder ${Component.cardTitle}`}
            >
              <DragIndicatorIcon fontSize="small" />
            </S.DragHandle>
          </Tooltip>
          <Tooltip title="Remove this card from the dashboard">
            <S.RemoveButton
              type="button"
              aria-label={`Remove ${Component.cardTitle}`}
              onClick={onRemove}
            >
              <RemoveIcon fontSize="small" />
            </S.RemoveButton>
          </Tooltip>
        </S.Controls>
      )}
    </S.Wrapper>
  )
}

export default DraggableCard

export const ADDED_FLASH_DURATION_MS = 1500

export const addedFlash = keyframes`
  0% {
    box-shadow: 0 0 0 4px ${color('cardAddedFlashBackground')}, 0 0 18px 6px ${color('cardAddedFlashBackground')};
  }
  100% {
    box-shadow: 0 0 0 0 transparent, 0 0 0 0 transparent;
  }
`

const S = {
  Wrapper: styled.div<{ $colSpan: 1 | 2; $isDragging: boolean; $isFlashing: boolean }>`
    position: relative;
    height: 100%;
    min-width: 0;
    grid-column: span ${({ $colSpan }) => $colSpan};
    opacity: ${({ $isDragging }) => ($isDragging ? 0.9 : 1)};
    transition: opacity 120ms ease;
    border-radius: 16px;
    animation: ${({ $isFlashing }) => ($isFlashing ? addedFlash : 'none')} ${ADDED_FLASH_DURATION_MS}ms ease-out;

    @media ${screenSize.large} {
      grid-column: span 1;
    }
  `,
  CardSlot: styled.div`
    height: 100%;

    > * {
      min-height: 100%;
      box-sizing: border-box;
    }
  `,
  Controls: styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 7px;
  `,
  DragHandle: styled.button`
    background: transparent;
    border: none;
    color: ${color('actionableIcon')};
    cursor: grab;
    border-radius: 4px;
    line-height: 0;

    svg {
      font-size: 1.75rem;
    }

    &:hover {
      color: ${color('actionableIconHover')};
      background: ${color('draggableCardBackground')};
    }

    &:active {
      cursor: grabbing;
    }

    &:focus-visible {
      outline: 2px solid ${color('primaryButton')};
      outline-offset: 2px;
    }
  `,
  RemoveButton: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: ${color('primaryButton')};
    border: none;
    color: ${color('primaryButtonText')};
    border-radius: 50%;
    cursor: pointer;

    &:hover {
      background: ${color('primaryButtonHover')};
    }

    &:focus-visible {
      outline: 2px solid ${color('primaryButton')};
      outline-offset: 2px;
    }
  `,
  DropIndicator: styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: -7px;
    width: 4px;
    border-radius: 2px;
    background-color: ${color('draggableDropIndicator')};
    z-index: 3;
    pointer-events: none;

    @media ${screenSize.large} {
      top: -7px;
      bottom: auto;
      left: 0;
      right: 0;
      width: auto;
      height: 4px;
    }
  `
}
